import { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Loader from '../Layouts/Loader';
import { Box, Typography, TextField, RadioGroup, FormControlLabel, Radio, Button, Avatar, Grid, Paper, CircularProgress, Alert } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MinCategory from '../Layouts/MinCategory';
import { useTheme } from '@mui/material/styles';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import MetaData from '../Layouts/MetaData';
import { clearErrors, loadUser, updateProfile } from '../../actions/userAction';
import { UPDATE_PROFILE_RESET } from '../../constants/userConstants';

const Account = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const { user, loading, isAuthenticated } = useSelector(state => state.user);

    const [avatar, setAvatar] = useState("");
    const { error, isUpdated, loading: updateLoading } = useSelector((state) => state.profile);
    const [profileData, setProfileData] = useState({
        firstName: '',
        lastName: '',
        gender: '',
        email: '',
        mobile: ''
    });
    const [avatarPreview, setAvatarPreview] = useState("");
    const [editMode, setEditMode] = useState(false);

    useEffect(() => {
        if (isAuthenticated === false) {
            navigate("/login");
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        if (user) {
            setProfileData(prevData => {
                const nameArray = user.name.split(" ");
                return {
                    ...prevData,
                    firstName: nameArray[0] || '',
                    lastName: nameArray.slice(1).join(" ") || '',
                    gender: user.gender || '',
                    email: user.email || '',
                    mobile: user.mobile || ''
                };
            });
            setAvatarPreview(user.avatar.url);
        }
        if (error) {
            console.error(error);
            dispatch(clearErrors());
        }
        if (isUpdated) {
            dispatch(loadUser());
            dispatch({ type: UPDATE_PROFILE_RESET });
        }
    }, [dispatch, error, user, isUpdated, navigate]);

    const handleEditClick = () => {
        setEditMode(true);
        setAvatar("");
    };

    const handleSaveClick = () => {
        const name = `${profileData.firstName} ${profileData.lastName}`;
        const formData = new FormData();
        formData.set("name", name);
        formData.set("avatar", avatar);
        formData.set("email", profileData.email);
        formData.set("gender", profileData.gender);
        formData.set("mobile", profileData.mobile);

        dispatch(updateProfile(formData));
    };

    const handleAvatarChange = (e) => {
        const file = e.target.files[0];

        const reader = new FileReader();
        setAvatar("");
        reader.onload = () => {
            if (reader.readyState === 2) {
                setAvatarPreview(reader.result);
                setAvatar(reader.result);
            }
        };

        reader.readAsDataURL(file);
    }

    const handleChange = (e) => {
        setProfileData({ ...profileData, [e.target.name]: e.target.value });
    };

    const theme = useTheme();

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: 'background.default', alignItems: 'center' }}>
                <MetaData title="My Profile" />
                <MinCategory />
                {loading ? <Loader /> : (
                    <Box sx={{ display: 'flex', flexGrow: 1, mt: 4, mx: 5, gap: 2, alignItems: 'flex-start' }}>
                        <Sidebar activeTab="profile" sx={{ mr: 2, width: 'auto', minHeight: '100%' }} />
                        <Box component={Paper} elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
                                My Profile
                            </Typography>

                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Avatar
                                    alt="Avatar Preview"
                                    src={user?.avatar?.url}
                                    sx={{ width: 80, height: 80, mr: 2, transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.1)' } }}
                                />
                                <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                                    {user?.name}
                                </Typography>
                            </Box>

                            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                            {editMode && (
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        mt: 2,
                                        mb: 2,
                                    }}
                                >
                                    <Avatar
                                        src={avatarPreview}
                                        sx={{ width: 56, height: 56, mr: 2, transition: 'transform 0.2s ease-in-out', '&:hover': { transform: 'scale(1.1)' } }}
                                    />
                                    <Button
                                        component="label"
                                        role={undefined}
                                        variant="contained"
                                        tabIndex={-1}
                                        disabled={updateLoading}
                                        sx={{ transition: 'transform 0.2s ease-in-out', '&:hover': { transform: 'scale(1.05)' } }}
                                    >
                                        {updateLoading ? <CircularProgress size={24} /> : 'Update Avatar'}
                                        <input type="file" onChange={handleAvatarChange} name="avatar" accept="image/*" style={{ display: 'none' }} />
                                    </Button>
                                </Box>
                            )}
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="First Name"
                                        name="firstName"
                                        value={profileData.firstName}
                                        onChange={handleChange}
                                        fullWidth
                                        margin="normal"
                                        disabled={!editMode || updateLoading}
                                        variant="outlined"
                                        InputProps={{ readOnly: !editMode || updateLoading }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Last Name"
                                        name="lastName"
                                        value={profileData.lastName}
                                        onChange={handleChange}
                                        fullWidth
                                        margin="normal"
                                        disabled={!editMode || updateLoading}
                                        variant="outlined"
                                        InputProps={{ readOnly: !editMode || updateLoading }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Email Address"
                                        name="email"
                                        value={profileData.email}
                                        onChange={handleChange}
                                        fullWidth
                                        margin="normal"
                                        disabled={!editMode || updateLoading}
                                        variant="outlined"
                                        InputProps={{ readOnly: !editMode || updateLoading }}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <PhoneInput
                                        country={'in'}
                                        value={profileData.mobile}
                                        onChange={(phone) => setProfileData({ ...profileData, mobile: phone })}
                                        disabled={!editMode || updateLoading}
                                        inputStyle={{ 
                                            width: '100%',
                                            height: '56px',
                                            fontSize: '16px',
                                            paddingLeft: '58px',
                                            borderRadius: '4px',
                                            border: '1px solid rgba(0, 0, 0, 0.23)',
                                            background: 'transparent',
                                        }}
                                        containerStyle={{ margin: '16px 0 8px' }}
                                        buttonStyle={{ 
                                            border: '1px solid rgba(0, 0, 0, 0.23)',
                                            borderRadius: '4px 0 0 4px',
                                            background: 'transparent',
                                        }}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle1" sx={{ mt: 2, color: 'text.primary' }}>
                                        Gender
                                    </Typography>
                                    <RadioGroup
                                        row
                                        aria-label="gender"
                                        name="gender"
                                        value={profileData.gender}
                                        onChange={handleChange}
                                        disabled={!editMode || updateLoading}
                                    >
                                        <FormControlLabel value="male" control={<Radio disabled={!editMode || updateLoading} />} label="Male" />
                                        <FormControlLabel value="female" control={<Radio disabled={!editMode || updateLoading} />} label="Female" />
                                    </RadioGroup>
                                </Grid>
                            </Grid>

                            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                                {!editMode ? (
                                <Button
                                    variant="contained"
                                    color="primary"
                                    startIcon={<EditIcon />}
                                    onClick={handleEditClick}
                                    sx={{ transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                                >
                                    Edit Profile
                                </Button>
                                ) : (
                                    <Button
                                        variant="contained"
                                        color="secondary"
                                        onClick={handleSaveClick}
                                        disabled={updateLoading}
                                        sx={{ transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.05)' } }}
                                    >
                                        {updateLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                                    </Button>
                                )}
                            </Box>

                            <Typography variant="body2" sx={{ mt: 2, textAlign: 'right' }}>
                                <Link to="/password/update" style={{ textDecoration: 'none', color: 'primary.main' }}>Change Password</Link>
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </>
    );
}

export default Account;
